import { AxiosError } from "axios";
import { TOKENS } from "@/libs/apis/onboarding/reactQueryCacheKeys";
import { ERROR_CODES } from "@/libs/error/types";
import { useQuery } from "@tanstack/react-query";
import { useCollectionData } from "./useCollectionData";
import { makeApiTokenMetadataCall } from "../services/api/getTokenMetadata";
import { TokenMetadataType } from "../types";
import { isNil, size } from "lodash";
import { useProductData } from "./useProductData";

export const useTokenMetadata = (
  tokenId: string,
  collectionSlug: string,
  productSlug: string,
) => {
  const { collectionData } = useCollectionData(collectionSlug);

  const chainId = collectionData?.chainId;
  const contractAddress = collectionData?.contractAddress;
  const { productData, isLoading: productIsLoading } =
    useProductData(productSlug);

  const { isLoading, error, data } = useQuery<
    TokenMetadataType,
    AxiosError,
    TokenMetadataType
  >(
    [TOKENS, tokenId],
    () => {
      return makeApiTokenMetadataCall(
        tokenId,
        chainId as number,
        contractAddress as string,
        productData?.sys.id as string,
      );
    },
    {
      retry: false,
      refetchOnMount: false,
      enabled:
        !!chainId &&
        !!contractAddress &&
        !productIsLoading &&
        !!productData?.sys.id &&
        !isNil(tokenId),
      useErrorBoundary: ({ response }) => {
        const anErrorOccurred =
          response !== undefined &&
          (response.status >= ERROR_CODES.INTERNAL_ERROR ||
            response.status === ERROR_CODES.NOT_FOUND ||
            response.status === ERROR_CODES.BAD_REQUEST);

        //@ts-expect-error
        const thereIsNoMetadata = size(response?.data.metadata) === 0;

        return anErrorOccurred || thereIsNoMetadata;
      },
    },
  );

  return { isLoading, error, data };
};
